import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Bell, Check, CheckCheck, Video } from "lucide-react";
import { fmtRelative } from "@/lib/format";
import { celebrate } from "@/lib/celebrate";
import { pollWhenVisible } from "@/lib/pollWhenVisible";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";

export default function NotificationsBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const pollRef = useRef(null);
  const wsRef = useRef(null);
  const { activeWorkspaceId } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    load();
    // Open WebSocket for live push
    const token = localStorage.getItem("co_token");
    if (token) {
      const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
      const wsUrl = `${base}/api/v1/ws?token=${encodeURIComponent(token)}${activeWorkspaceId ? `&workspace_id=${activeWorkspaceId}` : ""}`;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "notification" || msg.type === "activity") {
              load();
              // Toast the title for push-style surfacing so the user
              // sees the alert even without opening the bell.
              const n = msg.notification;
              if (msg.type === "notification" && n && n.title) {
                toast(n.title, {
                  description: n.body || undefined,
                  action: n.link ? {
                    label: "Open",
                    onClick: () => nav(n.link),
                  } : undefined,
                });
              }
            }
            if (msg.type === "celebrate" && msg.event) {
              celebrate(msg.event, msg.vars || {});
            }
            // R47 · Phase 3 — re-broadcast typed WS events as window events
            // so any page (Webmail, Inbox) can subscribe without opening
            // its own WS connection.
            if (msg && msg.type) {
              window.dispatchEvent(new CustomEvent("ws-event", { detail: msg }));
            }
          } catch (e) { /* ignore */ }
        };
      } catch (e) { /* fallback to polling */ }
    }
    // Polling fallback (in case WS drops). Feb 2026 R281 · Only ticks
    // while the tab is visible + catches up on visibility return.
    const stopPoll = pollWhenVisible(load, 30000);
    return () => {
      stopPoll();
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) { /* ignore */ }
      }
    };
  }, [activeWorkspaceId]);

  const markAllRead = async () => {
    await api.post("/notifications/mark-read");
    load();
  };

  const openItem = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
    }
    setOpen(false);
    if (n.link) nav(n.link);
    load();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger className="relative w-9 h-9 flex items-center justify-center rounded-sm hover:bg-muted transition" data-testid="notifications-bell">
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold leading-none flex items-center justify-center" data-testid="notifications-badge">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="label-eyebrow">Notifications</div>
          {data.unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:underline flex items-center" data-testid="notif-mark-read-btn">
              <CheckCheck className="w-3 h-3 mr-1" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-auto">
          {data.items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet.</div>
          )}
          {data.items.slice(0, 10).map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted/40 transition ${!n.read ? "bg-primary/5" : ""}`}
              data-testid={`notif-item-${n.id}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  {n.body && <div className="text-xs text-muted-foreground truncate">{n.body}</div>}
                  <div className="text-[10px] text-muted-foreground mt-1">{fmtRelative(n.created_at)}</div>
                </div>
                {n.meta?.meeting_link && (
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); window.open(n.meta.meeting_link, "_blank", "noopener"); }}
                    className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 bg-primary text-primary-foreground hover:opacity-90 mt-0.5"
                    data-testid={`notif-join-${n.id}`}
                  >
                    <Video className="w-3 h-3" /> Join
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
        <button
          onClick={() => { setOpen(false); nav("/app/notifications"); }}
          className="w-full px-4 py-2 text-center text-xs text-primary hover:bg-muted/40 border-t border-border"
          data-testid="notif-see-all-btn"
        >
          See all notifications →
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
